import React, { useState, useRef } from 'react';
import { Dialog, IconButton, CircularProgress, Portal } from '@mui/material';
import { X, Sparkles, Send, Mic, MicOff, Check, Loader2, User, Mail, Phone, MapPin, Building2, Table, Calendar, Database, FileText, IndianRupee, Hash, Link, Share2, Tag, CalendarClock, Briefcase, Info } from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Apiurl from '../../Helpers/apiUrl';
import AiPreviewModal from './AiPreviewModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { usePdf } from '../context/pdfContext';
import DownloadSummary from './DownloadSummary';

const TypewriterText = ({ text }) => {
    const [displayedWords, setDisplayedWords] = useState([]);
    
    React.useEffect(() => {
        const words = text.split(" ");
        let i = 0;
        setDisplayedWords([]);
        const timer = setInterval(() => {
            if (i < words.length) {
                setDisplayedWords(prev => [...prev, words[i]]);
                i++;
            } else {
                clearInterval(timer);
            }
        }, 150);
        return () => clearInterval(timer);
    }, [text]);
    
    return <span>{displayedWords.join(" ")}</span>;
};

export default function AiPromptModal({ isOpen, onClose, onOpen, onAddProducts, onRenameSelection, onCopySelectionCommand, onUpdateRowCommand, onDeleteRowCommand, onAddMultiSelections, masterProduct, clientOpt, onSelectClient, selections, activeSelectionIndex, apiKey, aiChatHistory = [], setAiChatHistory, onConfirmPreview, onSetGstType, onUpdateDiscount, onSaveQuote, onSaveDraft }) {
    const navigate = useNavigate();
    const reduxMasterProduct = useSelector((state) => state.product?.masterProduct || []);
    const effectiveMasterProduct = masterProduct || reduxMasterProduct;
    
    const [prompt, setPrompt] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [isStrictMatch, setIsStrictMatch] = useState(true);
    const [wasDictated, setWasDictated] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [templateFlowState, setTemplateFlowState] = useState({ active: false, quotationDetails: null, awaitingYesNo: false, awaitingClientName: false });
    const [saveFlowState, setSaveFlowState] = useState({ active: false, type: null });
    const [excelDownloadId, setExcelDownloadId] = useState(null);
    const { downloadPdf } = usePdf();
    const recognitionRef = useRef(null);
    const originalPromptRef = useRef("");
    const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 150 });
    const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, initialX: 0, initialY: 0 });
    const abortControllerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inactivityTimerRef = useRef(null);
    
    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => {
                    const lastMsg = prev[prev.length - 1];
                    if (lastMsg && lastMsg.text === "Is there anything else you want me to do?") return prev;
                    return [...prev, {
                        id: Date.now().toString() + 'conv',
                        sender: 'ai',
                        text: "Is there anything else you want me to do?",
                        timestamp: new Date().toISOString()
                    }];
                });
            }
        }, 300000);
    };

    React.useEffect(() => {
        const timer = setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 150);
        return () => clearTimeout(timer);
    }, [aiChatHistory, loading, isOpen]);

    React.useEffect(() => {
        if (isOpen) {
            resetInactivityTimer();
        } else {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        }
        return () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [isOpen]);

    React.useEffect(() => {
        const handleAiRefresh = () => {
            if (aiChatHistory.length > 0) {
                const latestPrompt = aiChatHistory.filter(msg => msg.sender === 'user').pop();
                if (latestPrompt) {
                    handleProcessPrompt(latestPrompt.text);
                }
            }
        };

        const handleDownloadComplete = (e) => {
            const { messageId, success, message } = e.detail;
            setAiChatHistory(prev => prev.map(msg => {
                if (msg.id === messageId) {
                    return {
                        ...msg,
                        isDownloading: false,
                        isSuccess: success,
                        isError: !success,
                        text: success ? (message || `Download completed successfully!`) : (message || `Failed to download.`)
                    };
                }
                return msg;
            }));
        };

        window.addEventListener('ai-refresh-data', handleAiRefresh);
        window.addEventListener('ai-download-complete', handleDownloadComplete);
        return () => {
            window.removeEventListener('ai-refresh-data', handleAiRefresh);
            window.removeEventListener('ai-download-complete', handleDownloadComplete);
        };
    }, [aiChatHistory]);

    React.useEffect(() => {
        const elementWidth = isOpen ? 400 : 64;
        const elementHeight = isOpen ? 700 : 64;
        setPosition(prev => ({
            x: Math.max(0, Math.min(prev.x, window.innerWidth - elementWidth)),
            y: Math.max(0, Math.min(prev.y, window.innerHeight - elementHeight))
        }));

        const handleResize = () => {
            const elW = isOpen ? 400 : 64;
            const elH = isOpen ? 700 : 64;
            setPosition(prev => ({
                x: Math.max(0, Math.min(prev.x, window.innerWidth - elW)),
                y: Math.max(0, Math.min(prev.y, window.innerHeight - elH))
            }));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    const handlePointerDown = (e) => {
        dragRef.current.isDragging = true;
        dragRef.current.startX = e.clientX || e.touches?.[0]?.clientX;
        dragRef.current.startY = e.clientY || e.touches?.[0]?.clientY;
        dragRef.current.initialX = position.x;
        dragRef.current.initialY = position.y;

        const handlePointerMove = (moveEvent) => {
            if (!dragRef.current.isDragging) return;
            const currentX = moveEvent.clientX || moveEvent.touches?.[0]?.clientX;
            const currentY = moveEvent.clientY || moveEvent.touches?.[0]?.clientY;
            const dx = currentX - dragRef.current.startX;
            const dy = currentY - dragRef.current.startY;
            
            let newX = dragRef.current.initialX + dx;
            let newY = dragRef.current.initialY + dy;
            
            // Constrain to window bounds depending on if it's open or closed
            const elementWidth = isOpen ? 400 : 64;
            const elementHeight = isOpen ? 700 : 64;
            newX = Math.max(0, Math.min(newX, window.innerWidth - elementWidth));
            newY = Math.max(0, Math.min(newY, window.innerHeight - elementHeight));

            setPosition({ x: newX, y: newY });
        };

        const handlePointerUp = (upEvent) => {
            dragRef.current.isDragging = false;
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('mouseup', handlePointerUp);
            window.removeEventListener('touchmove', handlePointerMove);
            window.removeEventListener('touchend', handlePointerUp);

            // If it was a click (didn't move much) and we are closed, open it!
            const upX = upEvent.clientX || upEvent.changedTouches?.[0]?.clientX || dragRef.current.startX;
            const upY = upEvent.clientY || upEvent.changedTouches?.[0]?.clientY || dragRef.current.startY;
            const dx = upX - dragRef.current.startX;
            const dy = upY - dragRef.current.startY;
            
            if (Math.abs(dx) < 5 && Math.abs(dy) < 5 && !isOpen) {
                if (typeof onOpen === 'function') onOpen();
            }
        };

        window.addEventListener('mousemove', handlePointerMove);
        window.addEventListener('mouseup', handlePointerUp);
        window.addEventListener('touchmove', handlePointerMove, { passive: false });
        window.addEventListener('touchend', handlePointerUp);
    };

    const prevIsOpen = useRef(isOpen);
    const prevIconPosition = useRef({ x: window.innerWidth - 80, y: window.innerHeight - 150 });

    React.useEffect(() => {
        if (isOpen && !prevIsOpen.current) {
            prevIconPosition.current = position;
            // Add Greeting if history is empty
            if (aiChatHistory.length === 0) {
                const hour = new Date().getHours();
                const greeting = hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening';
                const userName = localStorage.getItem("user_name") || "User";
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory([{
                        id: Date.now().toString(),
                        sender: 'ai',
                        text: `${greeting} ${userName}! How can I help you today?`,
                        timestamp: new Date().toISOString(),
                        isGreeting: true,
                        suggestions: [
                            "Select client Ruminate IT Solution create selection S1 and name it Master Bathroom Set. Add all SKUs with collection 'concept sphere' and finish 'matt black', set the quantity of each SKU to 4, and apply a 66% discount."
                        ]
                    }]);
                }
            }
            // Opening: shift so modal bottom-right is at icon's bottom-right
            setPosition(prev => {
                const elementWidth = 400;
                const elementHeight = 700;
                let newX = prev.x - elementWidth + 52;
                let newY = prev.y - elementHeight + 52;
                newX = Math.max(0, Math.min(newX, window.innerWidth - elementWidth));
                newY = Math.max(0, Math.min(newY, window.innerHeight - elementHeight));
                return { x: newX, y: newY };
            });
        } else if (!isOpen && prevIsOpen.current) {
            // Closing: restore exact icon position
            setPosition(prevIconPosition.current);
        }
        prevIsOpen.current = isOpen;
    }, [isOpen]);

    const handleCancel = () => {
        if (loading && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        onClose();
    };

    React.useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let sessionTranscript = '';
                for (let i = 0; i < event.results.length; ++i) {
                    sessionTranscript += event.results[i][0].transcript;
                }
                setPrompt(originalPromptRef.current + (originalPromptRef.current && sessionTranscript ? ' ' : '') + sessionTranscript);
                setWasDictated(true);
            };

            recognition.onerror = (event) => {
                console.error("Speech recognition error:", event.error);
                setIsListening(false);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            if (!recognitionRef.current) {
                toast.error("Your browser does not support speech recognition.");
                return;
            }
            originalPromptRef.current = prompt;
            try {
                recognitionRef.current.start();
                setIsListening(true);
            } catch (e) {
                console.error("Failed to start speech recognition:", e);
                toast.error("Microphone is already in use or permission denied.");
            }
        }
    };

    const handleAbort = (e) => {
        if (e) e.preventDefault();
        if (abortControllerRef.current) {
            abortControllerRef.current.isUserAbort = true;
            abortControllerRef.current.abort();
        }
        setLoading(false);
        setLoadingText("");
    };

    const handleProcessPrompt = async (overridePrompt = null) => {
        if (loading) return;
        
        const activePrompt = typeof overridePrompt === 'string' ? overridePrompt : prompt;

        // Allow empty prompt ONLY if awaiting client name for template
        if (!activePrompt.trim() && !(templateFlowState.active && templateFlowState.awaitingClientName)) {
            return;
        }
        
        const wordCount = activePrompt.trim() ? activePrompt.trim().split(/\s+/).length : 0;
        if (wordCount > 1500) {
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, { id: Date.now().toString(), sender: 'ai', text: 'Your request cannot exceed 1500 words.', isError: true, timestamp: new Date().toISOString() }]);
            }
            return;
        }

        const currentPrompt = activePrompt;
        if (typeof setAiChatHistory === 'function') {
            setAiChatHistory(prev => [...prev, { id: Date.now().toString(), sender: 'user', text: currentPrompt || "No Client", timestamp: new Date().toISOString() }]);
        }
        
        setPrompt(""); // Clear the input immediately after sending
        setIsListening(false);
        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }
        resetInactivityTimer();

        if (saveFlowState.active) {
            const promptLower = currentPrompt.toLowerCase().trim();
            const isAffirmative = /yes|y|sure|ok|do it/i.test(promptLower);
            
            if (isAffirmative) {
                if (saveFlowState.type === 'saveDraft' && typeof onSaveDraft === 'function') {
                    onSaveDraft();
                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'reply', sender: 'ai', text: "Opening the save draft window now...", timestamp: new Date().toISOString() }]);
                    }
                } else if (saveFlowState.type === 'saveQuotation' && typeof onSaveQuote === 'function') {
                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'reply', sender: 'ai', text: "Saving quotation...", timestamp: new Date().toISOString() }]);
                    }
                    const res = await onSaveQuote('new');
                    if (res?.success && typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'saved', sender: 'ai', text: `Quotation saved successfully with ID: ${res.projectId}\n\nShould I navigate you to the summary page?`, timestamp: new Date().toISOString() }]);
                    }
                } else if (saveFlowState.type === 'updateQuotation' && typeof onSaveQuote === 'function') {
                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'reply', sender: 'ai', text: "Updating quotation...", timestamp: new Date().toISOString() }]);
                    }
                    const res = await onSaveQuote('update');
                    if (res?.success && typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'updated', sender: 'ai', text: `Quotation updated successfully with ID: ${res.projectId}\n\nShould I navigate you to the summary page?`, timestamp: new Date().toISOString() }]);
                    }
                }
            } else {
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'reply', sender: 'ai', text: "Save cancelled.", timestamp: new Date().toISOString() }]);
                }
            }
            setSaveFlowState({ active: false, type: null });
            return;
        }

        // Check if the user is trying to delete a quotation
        const deleteMatch = currentPrompt.match(/(?:delete|remove|undo)\s+(?:this\s+)?(?:quotation\s+)?(P_.+?_\d{14}(?:_v\d+)?|PRJ-\d+)/i);
        if (deleteMatch && !currentPrompt.match(/row|item|product|sku/i)) {
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: 'I am not authorized to delete any quotation.',
                    timestamp: new Date().toISOString()
                }]);
            }
            return;
        }

        // Check for quotation format P_NAME_DATE_vX or PRJ-123 using a regex
        const quotationMatch = currentPrompt.match(/(?:P_.+?_\d{14}(?:_v\d+)?|PRJ-\d+)/i);
        if (quotationMatch && !templateFlowState.active) {
            const quotationId = quotationMatch[0].toUpperCase();
            setLoading(true);
            setLoadingText(`Searching for Quotation ID: ${quotationId}...`);
            
            try {
                // Determine the year from the quotation ID if possible, otherwise send empty to search all
                let yearParam = new Date().getFullYear();
                if (quotationId.startsWith('P_')) {
                    const yearMatch = quotationId.match(/_(\d{4})\d{10}(?:_v\d+)?$/);
                    if (yearMatch) yearParam = yearMatch[1];
                }
                const searchRes = await axios.get(`${Apiurl}project/summary?search=${quotationId}&year=${yearParam}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` }
                });
                
                const projects = searchRes.data?.data?.data || searchRes.data?.data || [];
                const matchedProject = projects.find(p => p.project_id?.toUpperCase() === quotationId || p.id?.toString() === quotationId.replace('PRJ-', ''));
                
                if (matchedProject) {
                    const detailRes = await axios.get(`${Apiurl}project/summary/project/${matchedProject.id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` }
                    });
                    
                    const selectionDetails = detailRes.data?.data || [];
                    const quoteDetails = detailRes.data?.client || {};
                    const totalListPrice = selectionDetails.reduce((sum, sel) => sum + parseFloat(sel.listPrice || 0), 0);
                    const totalOfferPrice = selectionDetails.reduce((sum, sel) => sum + parseFloat(sel.offerPrice || 0), 0);
                    
                    const quotationDetails = {
                        selectionDetails,
                        quoteDetails,
                        project: matchedProject,
                        totalListPrice,
                        totalOfferPrice
                    };
                    
                    let aiReply = `I found the quotation "${quotationId}". You can preview it, edit it, or use it as a template using the buttons below.`;
                    try {
                        const aiRes = await axios.post(`${Apiurl}prompt-assist`, {
                            prompt: currentPrompt,
                            context_data: {
                                type: "quotation_found",
                                quotationId: quotationId,
                                clientName: quoteDetails.client_name,
                                totalItems: selectionDetails.length
                            }
                        }, { headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` } });
                        
                        let rawText = aiRes.data?.choices?.[0]?.message?.content || '{}';
                        rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
                        const extracted = JSON.parse(rawText);
                        if (extracted.conversationalReply) {
                            aiReply = extracted.conversationalReply;
                        }
                    } catch (e) {
                        console.error("AI Context generation failed", e);
                    }

                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, {
                            id: Date.now().toString(),
                            sender: 'ai',
                            text: aiReply,
                            timestamp: new Date().toISOString(),
                            isQuotationCard: true,
                            quotationDetails: quotationDetails
                        }]);
                    }
                    
                    // We don't set templateFlowState here anymore, because the user will click a button in the Quotation Card to initiate the flow if they want.
                } else {
                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, {
                            id: Date.now().toString() + 'err',
                            sender: 'ai',
                            text: `I couldn't find any quotation matching the ID "${quotationId}".`,
                            isError: true,
                            timestamp: new Date().toISOString()
                        }]);
                    }
                }
            } catch (error) {
                console.error("Quotation search error:", error);
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString() + 'err',
                        sender: 'ai',
                        text: "An error occurred while searching for the quotation.",
                        isError: true,
                        timestamp: new Date().toISOString()
                    }]);
                }
            }
            
            setLoading(false);
            setLoadingText("");
            return;
        }

        if (templateFlowState.active && templateFlowState.awaitingClientName) {
            setLoading(true);
            setLoadingText("Processing client name...");
            const promptLower = currentPrompt.toLowerCase().trim();
            const isManualOrSkip = /manual|skip|don't know|no client|no idea|later/i.test(promptLower);

            if (isManualOrSkip) {
                const newHistory = [...aiChatHistory, {
                    id: Date.now().toString() + 'reply',
                    sender: 'ai',
                    text: "No problem, you can enter the client manually later. Loading template...",
                    timestamp: new Date().toISOString()
                }];
                setAiChatHistory(newHistory);
                setLoading(true);
                setLoadingText("Loading template...");
                setTimeout(() => {
                    navigate(`/user/create-quote`, { 
                        state: { 
                            selectionDetails: templateFlowState.quotationDetails.selectionDetails, 
                            quoteDetails: templateFlowState.quotationDetails.quoteDetails, 
                            isTemplate: true,
                            preserveChatHistory: true,
                            aiChatHistory: newHistory
                        } 
                    });
                    setTemplateFlowState({ active: false, quotationDetails: null, awaitingYesNo: false, awaitingClientName: false });
                    setLoading(false);
                }, 1500);
                return;
                return;
            }

            // Otherwise, assume they typed a client name and search the frontend list
            const matchedClient = clientOpt?.find(c => 
                c.client_name && String(c.client_name).toLowerCase().includes(promptLower)
            );
            
            if (matchedClient) {
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    isClientCard: true,
                    clientDetails: matchedClient,
                    isTemplateClient: true,
                    templateQuotationDetails: templateFlowState.quotationDetails,
                    timestamp: new Date().toISOString()
                }]);
            } else {
                const newHistory = [...aiChatHistory, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: `I couldn't find a client named "${currentPrompt}". Loading template as a blank quotation instead.`,
                    isSuccess: true,
                    timestamp: new Date().toISOString()
                }];
                setAiChatHistory(newHistory);
                setLoading(true);
                setLoadingText("Loading template...");
                setTimeout(() => {
                    navigate(`/user/create-quote`, { 
                        state: { 
                            selectionDetails: templateFlowState.quotationDetails.selectionDetails, 
                            quoteDetails: templateFlowState.quotationDetails.quoteDetails, 
                            isTemplate: true,
                            preserveChatHistory: true,
                            aiChatHistory: newHistory
                        } 
                    });
                    setTemplateFlowState({ active: false, quotationDetails: null, awaitingYesNo: false, awaitingClientName: false });
                    setLoading(false);
                }, 2000);
                return;
            }
            
            setLoading(false);
            setLoadingText("");
            return;
        }

        setLoading(true);
        setLoadingText("Analyzing your request...");

        try {
            const controller = new AbortController();
            abortControllerRef.current = controller;
            const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 seconds timeout

            const response = await fetch(
                `${Apiurl}prompt-assist`,
                {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('u_token')}`
                },
                body: JSON.stringify({
                    prompt: currentPrompt.replace(/\b(?:selection|tab)\s+(to|too|two)\b/gi, 'selection 2').replace(/\b(?:selection|tab)\s+(tree|three)\b/gi, 'selection 3').replace(/\b(?:selection|tab)\s+(for|four)\b/gi, 'selection 4') + (activeSelectionIndex ? `\n\n[SYSTEM NOTE: The user is currently viewing Selection Tab ${activeSelectionIndex}. If the user does not specify a selection tab for an action, you MUST output selectionIndex: ${activeSelectionIndex}]` : ''),
                    chat_history: aiChatHistory.slice(-4).map(msg => ({ sender: msg.sender, text: msg.text || (msg.isSuccess ? "Success" : "") }))
                }),
                signal: controller.signal
                }
            );
            clearTimeout(timeoutId);

            if (!response.ok) {
                let errData;
                try {
                    errData = await response.json();
                } catch (e) {}
                throw new Error(errData?.error?.message || 'Sorry, I didn\'t get that. Please try again.');
            }

            const result = await response.json();
            let rawText = result?.choices?.[0]?.message?.content || '{}';
            rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
            
            let extracted;
            try {
                extracted = JSON.parse(rawText);
            } catch (parseError) {
                console.error("AI JSON Parse Error:", parseError);
                console.log("Raw AI Output:", rawText);
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'errParse', sender: 'ai', text: 'The AI encountered an issue generating a valid response. Please try rephrasing your prompt.', isError: true, timestamp: new Date().toISOString() }]);
                }
                setLoading(false);
                setLoadingText("");
                return;
            }
            if (extracted.tabularData) {
                setLoadingText("Retrieving records...");
            } else if (extracted.selections && extracted.selections.length > 0) {
                setLoadingText("Extracting product selections...");
            } else {
                setLoadingText("Processing AI response...");
            }
            await new Promise(r => setTimeout(r, 600));
            
            if (extracted.error) {
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, { id: Date.now().toString() + 'errExt', sender: 'ai', text: extracted.error, isError: true, timestamp: new Date().toISOString() }]);
                }
                setLoading(false);
                return;
            }

            if (extracted.actionType === 'findClient') {
                const safeClientName = String(extracted.clientName || '').toLowerCase().trim();
                const matchedClient = clientOpt?.find(c => 
                    c.client_name && String(c.client_name).toLowerCase().includes(safeClientName)
                );
                
                if (matchedClient) {
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString(),
                        sender: 'ai',
                        isClientCard: true,
                        clientDetails: matchedClient,
                        timestamp: new Date().toISOString()
                    }]);
                } else {
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString(),
                        sender: 'ai',
                        isError: true,
                        text: `I couldn't find a client named "${extracted.clientName}" in your account. I can only show details for your own clients.`,
                        timestamp: new Date().toISOString()
                    }]);
                }
                setLoading(false);
                setLoadingText("");
                return;
            }

            if (extracted.actionType === 'findQuotation') {
                try {
                    setLoadingText("Fetching quotation details...");
                    const res = await axios.get(`${Apiurl}project/summary?search=${extracted.quotationId}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` }
                    });
                    
                    const projectList = res.data?.data?.data || res.data?.data || [];
                    const project = projectList.find(p => p.project_id === extracted.quotationId || p.project_id?.includes(extracted.quotationId)) || projectList[0];

                    if (project) {
                        const detailRes = await axios.get(`${Apiurl}project/summary/project/${project.id}`, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` }
                        });
                        const selectionDetails = detailRes.data?.data || [];
                        const quoteDetails = {
                            ...(detailRes.data?.client ?? {}),
                            gstType: detailRes.data?.client?.gst != 0 ? 'INC' : 'EXC'
                        };

                        let totalOfferPrice = 0;
                        let totalListPrice = 0;
                        selectionDetails.forEach(sel => {
                            totalOfferPrice += parseFloat(sel.offerPrice || 0);
                            totalListPrice += parseFloat(sel.listPrice || 0);
                        });

                        setAiChatHistory(prev => [...prev, {
                            id: Date.now().toString(),
                            sender: 'ai',
                            isQuotationCard: true,
                            quotationDetails: {
                                project,
                                selectionDetails,
                                quoteDetails,
                                totalOfferPrice,
                                totalListPrice
                            },
                            timestamp: new Date().toISOString()
                        }]);
                    } else {
                        setAiChatHistory(prev => [...prev, {
                            id: Date.now().toString(),
                            sender: 'ai',
                            isError: true,
                            text: `I couldn't find a quotation matching "${extracted.quotationId}".`,
                            timestamp: new Date().toISOString()
                        }]);
                    }
                } catch (error) {
                    console.error("Error fetching quotation:", error);
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString(),
                        sender: 'ai',
                        isError: true,
                        text: "An error occurred while fetching the quotation details.",
                        timestamp: new Date().toISOString()
                    }]);
                }
                setLoading(false);
                setLoadingText("");
                return;
            }

    const getLevenshteinDistance = (a, b) => {
        if(a.length === 0) return b.length; 
        if(b.length === 0) return a.length; 
        let matrix = [];
        for(let i = 0; i <= b.length; i++) matrix[i] = [i];
        for(let j = 0; j <= a.length; j++) matrix[0][j] = j;
        for(let i = 1; i <= b.length; i++){
            for(let j = 1; j <= a.length; j++){
                if(b.charAt(i-1) === a.charAt(j-1)){
                    matrix[i][j] = matrix[i-1][j-1];
                } else {
                    matrix[i][j] = Math.min(matrix[i-1][j-1] + 1, Math.min(matrix[i][j-1] + 1, matrix[i-1][j] + 1));
                }
            }
        }
        return matrix[b.length][a.length];
    };

    const isFuzzyMatch = (search, text) => {
        if (!search || !text) return false;
        search = String(search).toLowerCase();
        text = String(text).toLowerCase();
        if (text.includes(search)) return true;

        // Subsequence check
        let sClean = search.replace(/[^a-z0-9]/g, '');
        let tClean = text.replace(/[^a-z0-9]/g, '');
        if (sClean) {
            let sIdx = 0;
            for (let tIdx = 0; tIdx < tClean.length && sIdx < sClean.length; tIdx++) {
                if (tClean[tIdx] === sClean[sIdx]) sIdx++;
            }
            if (sIdx === sClean.length) return true;
        }

        // Edit distance word by word
        const searchWords = search.split(' ');
        const textWords = text.split(' ');
        
        return searchWords.every(sw => {
            if (sw.length < 4) return textWords.some(tw => tw === sw || tw.includes(sw));
            return textWords.some(tw => {
                if (tw.includes(sw)) return true;
                const maxDist = sw.length <= 5 ? 1 : 2;
                return getLevenshteinDistance(sw, tw) <= maxDist;
            });
        });
    };

    const allMappedSelections = [];
    const aiWarnings = [];

    // Process UI Commands first
    if (extracted.uiCommands && Array.isArray(extracted.uiCommands)) {
        let saveRequested = null;
        let navigationRequested = null;
        let clickRequested = null;
        let downloadTriggered = false;

        extracted.uiCommands.forEach(cmd => {
            try {
                extracted._processedUpdateRow = true;
                if (cmd.command === 'saveQuotation') {
                    saveRequested = 'saveQuotation';
                } else if (cmd.command === 'saveDraft') {
                    saveRequested = 'saveDraft';
                } else if (cmd.command === 'updateQuotation') {
                    saveRequested = 'updateQuotation';
                } else if (cmd.command === 'deleteRow' && typeof onDeleteRowCommand === 'function') {
                    let targetIdx = null;
                    if (cmd.selectionIndex !== undefined && cmd.selectionIndex !== null) {
                        targetIdx = cmd.selectionIndex - 1;
                        if (targetIdx < 0) targetIdx = 0;
                    }
                    
                    const processSerialNumber = (sNum) => {
                        onDeleteRowCommand(targetIdx, sNum, cmd.sku);
                    };

                    const rawSerialNumber = cmd.serialNumber || cmd.serialNumbers || cmd.serial_number || cmd.serial_numbers;
                    if (typeof rawSerialNumber === 'string' && rawSerialNumber.includes(',')) {
                        const cleanStr = rawSerialNumber.replace(/[\[\]]/g, '');
                        cleanStr.split(',').forEach(n => {
                            const parsed = parseInt(n.trim(), 10);
                            if (!isNaN(parsed)) processSerialNumber(parsed);
                        });
                    } else if (Array.isArray(rawSerialNumber)) {
                        rawSerialNumber.forEach(n => {
                            const parsed = parseInt(String(n).trim(), 10);
                            if (!isNaN(parsed)) processSerialNumber(parsed);
                        });
                    } else {
                        processSerialNumber(rawSerialNumber);
                    }
                } else if (cmd.command === 'setGst' && typeof onSetGstType === 'function') {
                    onSetGstType(cmd.gstType);
                } else if (cmd.command === 'globalDiscount' && typeof onUpdateDiscount === 'function') {
                    onUpdateDiscount('global', null, cmd.discountPercent || 0, cmd.discountPrice || 0, null);
                } else if (cmd.command === 'selectionDiscount' && typeof onUpdateDiscount === 'function') {
                    onUpdateDiscount('selection', (cmd.selectionIndex || 1) - 1, cmd.discountPercent || 0, cmd.discountPrice || 0, null);
                } else if (cmd.command === 'skuDiscount' && typeof onUpdateDiscount === 'function') {
                    onUpdateDiscount('sku', null, cmd.discountPercent || 0, cmd.discountPrice || 0, cmd.sku);
                } else if (cmd.command === 'renameSelection' && typeof onRenameSelection === 'function') {
                    onRenameSelection((cmd.selectionIndex || 1) - 1, cmd.newName);
                } else if (cmd.command === 'copySelection' && typeof onCopySelectionCommand === 'function') {
                    const srcIdx = cmd.sourceIndex !== undefined ? cmd.sourceIndex - 1 : null;
                    const tgtIdx = cmd.targetIndex !== undefined ? cmd.targetIndex - 1 : null;
                    onCopySelectionCommand(srcIdx, tgtIdx, cmd.newName);
                } else if (cmd.command === 'updateRow' && typeof onUpdateRowCommand === 'function') {
                    let targetIdx = null;
                    if (cmd.selectionIndex !== undefined && cmd.selectionIndex !== null) {
                        targetIdx = cmd.selectionIndex - 1;
                        if (targetIdx < 0) targetIdx = 0;
                    }
                    
                    const processSerialNumber = (sNum) => {
                        onUpdateRowCommand(
                            targetIdx,
                            sNum,
                            cmd.sku,
                            cmd.quantity,
                            cmd.discountPercent,
                            cmd.discountPrice
                        );
                    };

                    const rawSerialNumber = cmd.serialNumber || cmd.serialNumbers || cmd.serial_number || cmd.serial_numbers;
                    if (typeof rawSerialNumber === 'string' && rawSerialNumber.includes(',')) {
                        const cleanStr = rawSerialNumber.replace(/[\[\]]/g, '');
                        cleanStr.split(',').forEach(n => {
                            const parsed = parseInt(n.trim(), 10);
                            if (!isNaN(parsed)) processSerialNumber(parsed);
                        });
                    } else if (Array.isArray(rawSerialNumber)) {
                        rawSerialNumber.forEach(n => {
                            const parsed = parseInt(String(n).trim(), 10);
                            if (!isNaN(parsed)) processSerialNumber(parsed);
                        });
                    } else {
                        processSerialNumber(rawSerialNumber);
                    }
                } else if (cmd.command === 'navigate') {
                    let dest = cmd.path || cmd.destination;
                    if (dest) {
                        const pathMap = {
                            'home': '/user',
                            'dashboard': '/user/dashboard',
                            'add-client': '/user/add-client',
                            'view-client': '/user/viewclient',
                            'draft': '/user/draft',
                            'summary': '/user/summary',
                            'manual': '/user/manual',
                            'pdf-settings': '/user/pdf-settings',
                            'help-desk': '/user/help-desk'
                        };
                        navigationRequested = pathMap[dest] || dest;
                    }
                } else if (cmd.command === 'click' && cmd.text) {
                    clickRequested = cmd.text;

                } else if (cmd.command === 'downloadQuotation') {
                    downloadTriggered = true;
                    const messageId = Date.now().toString() + Math.random().toString(36).substring(7);
                    if (typeof setAiChatHistory === 'function') {
                        setAiChatHistory(prev => [...prev, {
                            id: messageId,
                            sender: 'ai',
                            text: `Downloading ${cmd.format.toUpperCase()}...`,
                            isDownloading: true,
                            format: cmd.format,
                            timestamp: new Date().toISOString()
                        }]);
                    }
                    
                    let idToDownload = null;
                    if (cmd.targetType === 'id') {
                        idToDownload = cmd.targetValue;
                    } else if (cmd.targetType === 'srno') {
                        const srno = parseInt(cmd.targetValue, 10);
                        if (!isNaN(srno) && extracted.tabularData) {
                            const index = srno - 1;
                            if (index >= 0 && index < extracted.tabularData.length) {
                                idToDownload = extracted.tabularData[index].project_id;
                            }
                        }
                    }

                    if (idToDownload) {
                        if (cmd.format === 'pdf') {
                            downloadPdf(idToDownload, `${Apiurl}user/downloadPdf`).then(() => {
                                setAiChatHistory(prev => prev.map(msg => msg.id === messageId ? { ...msg, isDownloading: false, text: "Downloaded PDF successfully!" } : msg));
                            }).catch(() => {
                                setAiChatHistory(prev => prev.map(msg => msg.id === messageId ? { ...msg, isDownloading: false, text: "Failed to download PDF." } : msg));
                            });
                        } else if (cmd.format === 'excel') {
                            setExcelDownloadId({ id: idToDownload, messageId });
                        }
                    } else {
                        setAiChatHistory(prev => prev.map(msg => msg.id === messageId ? { ...msg, isDownloading: false, text: "Could not find the quotation to download." } : msg));
                    }
                }
            } catch (e) {
                console.error("Failed to execute UI command", cmd, e);
            }
        });

        if (clickRequested) {
            const searchStr = clickRequested.toLowerCase().trim();
            const clickableElements = Array.from(document.querySelectorAll('button, a, [role="button"]'));
            
            let targetElement = clickableElements.find(el => el.innerText?.toLowerCase().trim() === searchStr);
            if (!targetElement) {
                targetElement = clickableElements.find(el => el.innerText?.toLowerCase().includes(searchStr));
            }
            
            if (targetElement) {
                targetElement.click();
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString(),
                        sender: 'ai',
                        text: extracted.conversationalReply ? `${extracted.conversationalReply}\n\nClicked "${targetElement.innerText?.trim()}" successfully.` : `Clicked "${targetElement.innerText?.trim()}" successfully.`,
                        timestamp: new Date().toISOString()
                    }]);
                }
            } else {
                if (typeof setAiChatHistory === 'function') {
                    setAiChatHistory(prev => [...prev, {
                        id: Date.now().toString(),
                        sender: 'ai',
                        text: `Could not find a button matching "${clickRequested}" on the screen.`,
                        timestamp: new Date().toISOString()
                    }]);
                }
            }
            setLoading(false);
            setLoadingText("");
            return;
        }

        if (navigationRequested) {
            navigate(navigationRequested);
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: extracted.conversationalReply ? `${extracted.conversationalReply}\n\nNavigating to ${navigationRequested}...` : `Navigating to ${navigationRequested}...`,
                    timestamp: new Date().toISOString()
                }]);
            }
            setLoading(false);
            setLoadingText("");
            return;
        }

        if (saveRequested) {
            setSaveFlowState({ active: true, type: saveRequested });
            if (typeof setAiChatHistory === 'function') {
                const message = saveRequested === 'saveDraft' 
                    ? "Are you sure you want to save this to your drafts? (Yes/No)"
                    : (saveRequested === 'updateQuotation' ? "Are you sure you want to update this quotation? (Yes/No)" : "Are you sure you want to save this quotation? (Yes/No)");
                    
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: extracted.conversationalReply ? `${extracted.conversationalReply}\n\n${message}` : message,
                    timestamp: new Date().toISOString()
                }]);
            }
            setLoading(false);
            setLoadingText("");
            return;
        }

        // If there were UI commands but no save requested, and no selections to process, we can just reply
        if (!extracted.selections || extracted.selections.length === 0) {
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: extracted.conversationalReply || "I have updated the quotation as requested.",
                    timestamp: new Date().toISOString()
                }]);
            }
            setLoading(false);
            setLoadingText("");
            return;
        }
    }
    
    if (extracted.selections && extracted.selections.length > 0) {
        setLoadingText("Matching SKUs in catalog...");
        await new Promise(r => setTimeout(r, 600));
    }

    (extracted.selections || []).forEach(sel => {
        const selectionMatchedProducts = [];

        (sel.actions || []).forEach(action => {
            if (action.actionType === 'updateRow') {
                if (typeof onUpdateRowCommand === 'function') {
                    let targetIdx = null;
                    if (action.selectionIndex !== undefined && action.selectionIndex !== null) {
                        targetIdx = action.selectionIndex - 1;
                        if (targetIdx < 0) targetIdx = 0;
                    }

                    const processActionSerialNumber = (sNum) => {
                        onUpdateRowCommand(
                            targetIdx,
                            sNum,
                            action.sku,
                            action.quantity,
                            action.discountPercent,
                            action.discountPrice
                        );
                    };

                    const rawSerialNumber = action.serialNumber || action.serialNumbers || action.serial_number || action.serial_numbers;
                    if (typeof rawSerialNumber === 'string' && rawSerialNumber.includes(',')) {
                        const cleanStr = rawSerialNumber.replace(/[\[\]]/g, '');
                        cleanStr.split(',').forEach(n => {
                            const parsed = parseInt(n.trim(), 10);
                            if (!isNaN(parsed)) processActionSerialNumber(parsed);
                        });
                    } else if (Array.isArray(rawSerialNumber)) {
                        rawSerialNumber.forEach(n => {
                            const parsed = parseInt(String(n).trim(), 10);
                            if (!isNaN(parsed)) processActionSerialNumber(parsed);
                        });
                    } else {
                        processActionSerialNumber(rawSerialNumber);
                    }
                }
                extracted._processedUpdateRow = true;
                return;
            }

            let matched = effectiveMasterProduct;
            
            const filters = action.filters || {};
            if (action.matchType && action.matchValue) {
                filters[action.matchType] = action.matchValue;
            }
            
            if (filters['GLOBAL']) {
                const genericVal = String(filters['GLOBAL']).toLowerCase().trim();
                if (['products', 'product', 'items', 'item', 'all'].includes(genericVal)) {
                    delete filters['GLOBAL'];
                }
            }

            if (Object.keys(filters).length === 0 && !action.minPrice && !action.maxPrice) {
                matched = [];
            }

            Object.entries(filters).forEach(([type, value]) => {
                let currentMatched = [];
                let valStr = String(value).replace(/^["']|["']$/g, '').trim();
                valStr = valStr.replace(/^(collection|category)\s+/i, '').trim();
                valStr = valStr.replace(/\s+(collection|category)$/i, '').trim();
                const lowerVal = valStr.toLowerCase();

                if (type === 'GLOBAL') {
                    currentMatched = matched.filter(p => 
                        (p.collection && p.collection.toLowerCase().includes(lowerVal)) ||
                        (p.category && p.category.toLowerCase().includes(lowerVal)) ||
                        (p.finish && p.finish.toLowerCase().includes(lowerVal)) ||
                        (p.priceSegment && p.priceSegment.toLowerCase().includes(lowerVal)) ||
                        (p.product_typ_group && p.product_typ_group.toLowerCase().includes(lowerVal)) ||
                        (p.product_desc && p.product_desc.toLowerCase().includes(lowerVal)) ||
                        (p.sku_code && p.sku_code.toLowerCase().includes(lowerVal))
                    );
                    if (currentMatched.length === 0 && !isStrictMatch) {
                        const terms = lowerVal.split(' ').filter(w => w.length >= 3);
                        if (terms.length > 0) {
                            currentMatched = matched.filter(p => 
                                terms.every(t => 
                                    (p.collection && p.collection.toLowerCase().includes(t)) ||
                                    (p.category && p.category.toLowerCase().includes(t)) ||
                                    (p.product_desc && p.product_desc.toLowerCase().includes(t))
                                )
                            );
                        }
                    }
                    if (currentMatched.length === 0 && !isStrictMatch) {
                        currentMatched = matched.filter(p => 
                            (p.collection && isFuzzyMatch(lowerVal, p.collection)) ||
                            (p.category && isFuzzyMatch(lowerVal, p.category)) ||
                            (p.product_desc && isFuzzyMatch(lowerVal, p.product_desc))
                        );
                        if (currentMatched.length === 0) {
                            aiWarnings.push(`Keyword "${valStr}"`);
                        }
                    }
                } else if (type === 'SKU') {
                    const exactMatches = matched.filter(p => p.sku_code && p.sku_code.trim().toLowerCase() === valStr.toLowerCase());
                    if (exactMatches.length > 0) {
                        currentMatched = exactMatches;
                    } else if (!isStrictMatch) {
                        currentMatched = matched.filter(p => p.sku_code && p.sku_code.toLowerCase().includes(lowerVal));
                    }
                    
                    if (currentMatched.length === 0 && !isStrictMatch) {
                        // Fallback: The AI might have categorized a Collection as an SKU
                        currentMatched = matched.filter(p => p.collection && p.collection.toLowerCase().includes(lowerVal));
                        if (currentMatched.length === 0) {
                            const terms = lowerVal.split(' ').filter(w => w.length >= 4);
                            if (terms.length > 0) {
                                currentMatched = matched.filter(p => p.collection && terms.every(t => p.collection.toLowerCase().includes(t)));
                            }
                        }
                        if (currentMatched.length === 0) {
                            currentMatched = matched.filter(p => p.collection && isFuzzyMatch(lowerVal, p.collection));
                            if (currentMatched.length === 0) {
                                aiWarnings.push(`Item "${valStr}"`);
                            }
                        }
                    }
                } else {
                    const typeToProp = {
                        'COLLECTION': 'collection',
                        'CATEGORY': 'category',
                        'FINISH': 'finish',
                        'SEGMENT': 'priceSegment',
                        'TYPE': 'product_typ_group',
                        'DESCRIPTION': 'product_desc'
                    };
                    const propName = typeToProp[type] || Object.keys(effectiveMasterProduct[0] || {}).find(key => key.toLowerCase() === type.toLowerCase());
                    
                    if (propName) {
                        currentMatched = matched.filter(p => p[propName] && String(p[propName]).toLowerCase().includes(lowerVal));
                        
                        if (currentMatched.length === 0 && !isStrictMatch) {
                            const terms = lowerVal.split(' ').filter(w => w.length >= 4);
                            if (terms.length > 0) {
                                currentMatched = matched.filter(p => p[propName] && terms.every(t => String(p[propName]).toLowerCase().includes(t)));
                            }
                        }
                        if (currentMatched.length === 0 && !isStrictMatch) {
                            currentMatched = matched.filter(p => p[propName] && isFuzzyMatch(lowerVal, String(p[propName])));
                        }
                        if (currentMatched.length === 0 && !isStrictMatch) {
                            currentMatched = matched.filter(p => p.product_desc && p.product_desc.toLowerCase().includes(lowerVal));
                        }
                        if (currentMatched.length === 0) {
                            aiWarnings.push(`${type.charAt(0) + type.slice(1).toLowerCase()} "${valStr}"`);
                        }
                    } else {
                        // Generic fallback: Search all string properties
                        currentMatched = matched.filter(p => {
                            return Object.values(p).some(val => val && typeof val === 'string' && val.toLowerCase().includes(lowerVal));
                        });
                        if (currentMatched.length === 0) {
                            aiWarnings.push(`${type} "${valStr}"`);
                        }
                    }
                }

                // AND logic: only keep items that matched this filter
                matched = currentMatched;
                // If no matches after filter but a price constraint exists, start from full catalog
                if (matched.length === 0 && (action.minPrice || action.maxPrice)) {
                    matched = masterProduct;
                }
            });

            if (action.maxPrice) {
                matched = matched.filter(match => {
                    const price = Number(String(match.mrp || '0').replace(/,/g, ''));
                    return !isNaN(price) && price <= action.maxPrice;
                });
                if (matched.length === 0) {
                    aiWarnings.push(`Budget limit under ${action.maxPrice}`);
                }
            }
            if (action.minPrice) {
                matched = matched.filter(match => {
                    const price = Number(String(match.mrp || '0').replace(/,/g, ''));
                    return !isNaN(price) && price >= action.minPrice;
                });
                if (matched.length === 0) {
                    aiWarnings.push(`Price minimum over ${action.minPrice}`);
                }
            }

            matched.forEach(match => {
                selectionMatchedProducts.push({
                    productCode: match.sku_code || '',
                    brand: match.brand || '',
                    description: match.product_desc || '',
                    collection: match.collection || '',
                    productType: match.product_typ_group || '',
                    category: match.category || '',
                    priceSegment: match.priceSegment || '',
                    finish: match.finish || '',
                    image: match.image || null,
                    mrp: Number(String(match.mrp || '0').replace(/,/g, '')) || 0,
                    basePrice: Number(String(match.basePrice || '0').replace(/,/g, '')) || 0,
                    gstRate: Number(match.gst) || 18,
                    stock: Number(match.stockAvailable) ?? null,
                    stockIndicator: match.stockIndicator || '',
                    quantity: action.quantity || 1,
                    discountPercent: action.discountPercent || 0,
                    discountPrice: action.discountPrice || 0,
                    actionType: action.actionType || 'add',
                    area: action.area || ''
                });
            });
        });
        
        // Deduplicate matched products by productCode to prevent identical SKUs from appearing multiple times
        const uniqueProducts = [];
        const seenCodes = new Set();
        selectionMatchedProducts.forEach(p => {
            if (p.productCode && !seenCodes.has(p.productCode)) {
                seenCodes.add(p.productCode);
                uniqueProducts.push(p);
            } else if (!p.productCode) {
                uniqueProducts.push(p);
            }
        });

        if (uniqueProducts.length > 0) {
            allMappedSelections.push({
                selectionIndex: sel.selectionIndex || 0,
                selectionName: sel.selectionName || '',
                isNewSelection: sel.isNewSelection || false,
                products: uniqueProducts
            });
        }
    });

    setLoadingText("Finalizing quotation results...");
    await new Promise(r => setTimeout(r, 500));

    const extractedClientName = extracted.clientName || null;

    if (allMappedSelections.length > 0) {
        setLoadingText("Success!");
        setIsSuccess(true);
        const totalProductsAdded = allMappedSelections.flatMap(s => s.products).length;
        
        let successMessage = extracted.conversationalReply || `✨ Magic! AI found ${totalProductsAdded} products.`;
        
        if (typeof setAiChatHistory === 'function') {
            const messageId = Date.now().toString() + 'succ';
            setAiChatHistory(prev => [...prev, { 
                id: messageId, 
                sender: 'ai', 
                text: successMessage,
                isSuccess: true,
                isProductSelection: true,
                payload: allMappedSelections,
                clientName: extractedClientName,
                warnings: aiWarnings,
                timestamp: new Date().toISOString()
            }]);
            
            // Auto-open preview modal after a short delay
            setTimeout(() => {
                setPreviewData({ payload: allMappedSelections, warnings: aiWarnings, clientName: extractedClientName, messageId: messageId });
            }, 1000);
        }

        setTimeout(() => {
            setIsSuccess(false);
            setLoading(false);
            setLoadingText("");
        }, 1500);
    } else {
        const hasSelections = extracted.selections && extracted.selections.length > 0;
        const hasTabularData = extracted.tabularData && extracted.tabularData.length > 0;
        const isProfileCard = extracted.isProfileCard === true;
        const failedToFindProducts = hasSelections && allMappedSelections.length === 0;
        
        // --- PRIMARY AI RESPONSE ---
        if ((!hasSelections || extracted._processedUpdateRow) && (extracted.conversationalReply || hasTabularData || isProfileCard) && !failedToFindProducts) {
            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, {
                    id: Date.now().toString() + '_main',
                    sender: 'ai',
                    text: extracted.conversationalReply || "Here is the information you requested:",
                    tabularData: extracted.tabularData,
                    isProfileCard: isProfileCard,
                    profileDetails: extracted.profileDetails,
                    timestamp: new Date().toISOString()
                }]);
            }
            if (extracted._processedUpdateRow) {
                setLoading(false);
                setLoadingText("");
                return;
            }
        }

        // --- GLOBAL SEARCH FALLBACK (CLIENT MATCH) ---
        const safePromptFallback = currentPrompt.toLowerCase().trim();
        const promptWords = safePromptFallback.split(/\s+/).filter(w => w.length > 3 && !['show', 'client', 'details', 'please', 'find', 'search', 'what', 'about', 'me', 'with', 'this', 'that'].includes(w));
        
        const fallbackClientMatch = clientOpt?.find(c => {
            if (!c.client_name) return false;
            const clientNameLower = String(c.client_name).toLowerCase().trim();
            if (safePromptFallback.includes(clientNameLower)) return true;
            for (let w of promptWords) {
                // Must be an exact match or part of the name as a whole word, not just a substring (like 'and' in 'sandeep')
                if (clientNameLower === w || clientNameLower.split(/\s+/).includes(w)) return true;
            }
            return false;
        });

        if (fallbackClientMatch) {
            if (typeof setAiChatHistory === 'function') {
                setTimeout(() => {
                    setAiChatHistory(prev => {
                        // Avoid duplicate client cards if one was already added recently
                        if (prev.length > 0 && prev[prev.length-1].isClientCard && prev[prev.length-1].clientDetails?.id === fallbackClientMatch.id) return prev;
                        return [...prev, {
                            id: Date.now().toString() + '_client',
                            sender: 'ai',
                            text: `I also found the client details for "${fallbackClientMatch.client_name}".`,
                            isClientCard: true,
                            clientDetails: fallbackClientMatch,
                            timestamp: new Date().toISOString()
                        }];
                    });
                }, 100); // Slight delay so it appears after the main response
            }
        } else if (failedToFindProducts || (!hasSelections && !extracted.conversationalReply && !hasTabularData)) {
            // Completely unhandled request or failed product search
            const searchedValues = extracted.selections?.flatMap(s => s.actions.map(a => {
                const filterStrings = Object.entries(a.filters || {}).map(([k, v]) => `${k}="${v}"`);
                if (a.minPrice) filterStrings.push(`MRP >= ${a.minPrice}`);
                if (a.maxPrice) filterStrings.push(`MRP <= ${a.maxPrice}`);
                if (filterStrings.length > 0) return filterStrings.join(" AND ");
                
                const fallbackMatch = String(a.matchValue || "").toLowerCase().trim();
                if (['products', 'product', 'items', 'item', 'all'].includes(fallbackMatch)) return "";
                return a.matchValue || "";
            })).filter(Boolean).join(" OR ") || currentPrompt;

            let finalErrorMessage = `I couldn't find any products in your catalog matching your specific combination: ${searchedValues}. Try adjusting your search or checking for typos!`;
            let isFinalError = true;

            // Only run general fallback if the AI didn't already explicitly try to find products
            if (!hasSelections) {
                setLoadingText("Checking with AI assistant...");
                try {
                    const fallbackRes = await axios.post(`${Apiurl}prompt-assist`, {
                        prompt: currentPrompt,
                        context_data: { type: "general_fallback", searchedValues }
                    }, { headers: { Authorization: `Bearer ${localStorage.getItem('u_token')}` } });
                    
                    let rawText = fallbackRes.data?.choices?.[0]?.message?.content || '{}';
                    rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
                    const fallbackExtracted = JSON.parse(rawText);
                    
                    if (fallbackExtracted.conversationalReply) {
                        finalErrorMessage = fallbackExtracted.conversationalReply;
                        isFinalError = false; 
                    }
                } catch (e) {
                    console.error("General fallback failed", e);
                }
            }

            if (typeof setAiChatHistory === 'function') {
                setAiChatHistory(prev => [...prev, { 
                    id: Date.now().toString() + 'err2', 
                    sender: 'ai', 
                    text: finalErrorMessage,
                    isError: isFinalError,
                    timestamp: new Date().toISOString()
                }]);
            }

        }
      }
  } catch (error) {
    console.error("AI REST Error:", error);
    let errMsg = error.message || "Failed to process request. Please check your console log.";
    if (error.name === 'AbortError') {
        if (abortControllerRef.current?.isUserAbort) {
            errMsg = 'Request aborted by user.';
        } else {
            errMsg = 'Timeout: The AI took too long to respond. The AI server might be experiencing high load. Please try again.';
        }
    }
    
    if (typeof setAiChatHistory === 'function') {
        setAiChatHistory(prev => [...prev, { 
            id: Date.now().toString() + 'errApi', 
            sender: 'ai', 
            text: errMsg,
            isError: true,
            timestamp: new Date().toISOString()
        }]);
    }
  } finally {
    setLoading(false);
  }
};

    if (!isOpen) {
        return (
            <Portal>
                <div 
                    className="fixed z-[1050] cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-full"
                    style={{ left: `${position.x}px`, top: `${position.y}px` }}
                    onPointerDown={handlePointerDown}
                >
                    <div className="relative inline-flex items-center justify-center p-[2px] overflow-hidden rounded-full group bg-gradient-to-r from-orange-500 to-yellow-500 pointer-events-none">
                        <span className="absolute inset-[-1000%] animate-[spin_3s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#fef08a_0%,#ea580c_50%,#fef08a_100%)]" />
                        <div className="relative flex items-center justify-center bg-white p-3 rounded-full hover:bg-orange-50 transition-colors">
                            <Sparkles size={24} className="text-orange-500 pointer-events-none animate-pulse" />
                        </div>
                    </div>
                </div>
                {excelDownloadId && (
                <div style={{ display: 'none' }}>
                    <DownloadSummary 
                        project={excelDownloadId.id} 
                        sendData={() => {
                            if (excelDownloadId.messageId) {
                                setAiChatHistory(prev => prev.map(msg => msg.id === excelDownloadId.messageId ? { ...msg, isDownloading: false, text: "Downloaded EXCEL successfully!" } : msg));
                            }
                            setExcelDownloadId(null);
                        }} 
                    />
                </div>
            )}
            </Portal>
        );
    }

    return (
        <Portal>
            <div 
                className="fixed bg-slate-50 shadow-2xl flex flex-col z-[1040] rounded-xl border border-slate-200 overflow-hidden"
                style={{ 
                    fontFamily: "'DM Sans', sans-serif", 
                    width: '400px', 
                    height: '700px',
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                }}
            >
                <div 
                    className="flex items-center justify-between p-4 border-b border-slate-200 bg-white shadow-sm shrink-0 cursor-grab active:cursor-grabbing"
                    onPointerDown={handlePointerDown}
                >
                    <div className="flex items-center gap-2 pointer-events-none">
                        <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center">
                            <Sparkles size={15} className="text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-slate-900">Quotation AI Assistant</h2>
                    </div>
                    <IconButton onClick={handleCancel} size="small" sx={{ pointerEvents: 'auto' }}><X size={16} /></IconButton>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 relative">
                    {aiChatHistory.length === 0 ? (
                        <>
                            <div className="mb-2">
                                <p className="text-xs font-bold text-slate-700">Example prompt:</p>
                            </div>

                            <div 
                                onClick={() => setPrompt("Select client Ruminate IT Solution create selection S1 and name it Master Bathroom Set. Add all SKUs with collection 'concept sphere' and finish 'matt black', set the quantity of each SKU to 4, and apply a 66% discount.")}
                                className="bg-slate-50 p-2.5 rounded-lg text-[11px] font-medium text-slate-600 border-l-4 border-orange-500 italic cursor-pointer hover:bg-orange-50 transition-colors"
                            >
                                Select client Ruminate IT Solution create selection S1 and name it Master Bathroom Set. Add all SKUs with collection 'concept sphere' and finish 'matt black', set the quantity of each SKU to 4, and apply a 66% discount.
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col gap-4 pb-4">
                            {aiChatHistory.map((msg) => (
                                <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                                    {msg.payload ? (
                                        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm p-4 w-full max-w-[95%] mb-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                                                    <Sparkles size={12} className="text-orange-600" />
                                                </div>
                                                <span className="text-[12px] font-bold text-slate-700">Products Extracted</span>
                                            </div>
                                            <p className="text-[12px] text-slate-500 mb-3">
                                                {msg.payload.some(sel => sel.products && sel.products.length > 0)
                                                    ? `Found ${msg.payload.reduce((acc, curr) => acc + (curr.products?.length || 0), 0)} items across ${msg.payload.length} selections.`
                                                    : msg.applied
                                                        ? `Products were successfully added to your quotation.`
                                                        : msg.cleared
                                                            ? `This session has expired. Product data from previous sessions is cleared to save space.`
                                                            : `No matching products were found based on your request.`}
                                            </p>
                                            <button 
                                                onClick={() => setPreviewData({ payload: msg.payload, warnings: msg.warnings, clientName: msg.clientName, messageId: msg.id })}
                                                disabled={msg.applied || !msg.payload.some(sel => sel.products && sel.products.length > 0)}
                                                className={`w-full py-2 rounded-xl text-[12px] font-bold transition-all ${
                                                    msg.applied || !msg.payload.some(sel => sel.products && sel.products.length > 0)
                                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                    : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
                                                }`}
                                            >
                                                {msg.applied ? 'Added to Quote' : !msg.payload.some(sel => sel.products && sel.products.length > 0) ? 'Session Expired' : 'Preview & Add to Quote'}
                                            </button>
                                        </div>
                                    ) : msg.isProfileCard ? (
                                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm w-full max-w-md relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
                                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                                                <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                                                    <User size={18} />
                                                </div>
                                                <h4 className="font-bold text-slate-800 text-base">User Profile</h4>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4">
                                                {msg.profileDetails && Object.entries(msg.profileDetails).map(([key, val]) => (
                                                    val && val !== 'N/A' && (
                                                        <div key={key} className={`col-span-2 ${['Email', 'Phone'].includes(key) ? 'sm:col-span-1' : ''} flex items-start gap-2`}>
                                                            {key === 'Name' && <User size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            {key === 'Email' && <Mail size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            {key === 'Phone' && <Phone size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            {['City', 'State'].includes(key) && <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            {['Role / Designation', 'Sales Enabler'].includes(key) && <Building2 size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            {key === 'Employee Code' && <User size={14} className="text-slate-400 mt-0.5 shrink-0" />}
                                                            <div>
                                                                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{key}</p>
                                                                <p className="text-sm font-medium text-slate-800 break-all">{val}</p>
                                                            </div>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    ) : msg.isClientCard ? (
                                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm w-full max-w-md relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-400 to-orange-600"></div>
                                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                                                <div className="p-1.5 bg-orange-100 text-orange-600 rounded-lg">
                                                    <User size={18} />
                                                </div>
                                                <h4 className="font-bold text-slate-800 text-base">Client Details</h4>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4">
                                                {msg.clientDetails.client_name && <div className="col-span-2 flex items-start gap-2"><User size={14} className="text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Name</p><p className="text-sm font-medium text-slate-800">{msg.clientDetails.client_name}</p></div></div>}
                                                {msg.clientDetails.email && <div className="col-span-2 sm:col-span-1 flex items-start gap-2"><Mail size={14} className="text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Email</p><p className="text-sm font-medium text-slate-800 break-all">{msg.clientDetails.email}</p></div></div>}
                                                {msg.clientDetails.phone && <div className="col-span-2 sm:col-span-1 flex items-start gap-2"><Phone size={14} className="text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Phone</p><p className="text-sm font-medium text-slate-800">{msg.clientDetails.phone}</p></div></div>}
                                                {msg.clientDetails.afd && <div className="col-span-2 flex items-start gap-2"><Building2 size={14} className="text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Firm Name</p><p className="text-sm font-medium text-slate-800">{msg.clientDetails.afd}</p></div></div>}
                                                {(msg.clientDetails.site_add || msg.clientDetails.city) && <div className="col-span-2 flex items-start gap-2"><MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" /><div><p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Address</p><p className="text-sm font-medium text-slate-800">{[msg.clientDetails.site_add, msg.clientDetails.city, msg.clientDetails.state, msg.clientDetails.pin].filter(Boolean).join(", ")}</p></div></div>}
                                            </div>
                                            <button 
                                                className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-1.5 px-3 rounded-md transition-colors flex items-center justify-center gap-2"
                                                onClick={() => {
                                                    if (msg.isTemplateClient && msg.templateQuotationDetails) {
                                                        const clientName = msg.clientDetails.client_name;
                                                        const clientId = msg.clientDetails.id;
                                                        const newHistory = [...aiChatHistory, {
                                                            id: Date.now().toString(),
                                                            sender: 'ai',
                                                            isSuccess: true,
                                                            text: `Client "${clientName}" automatically selected for this quote. Loading template...`,
                                                            timestamp: new Date().toISOString()
                                                        }];
                                                        setAiChatHistory(newHistory);
                                                        navigate(`/user/create-quote/${clientId}/${encodeURIComponent(clientName)}/new`, { 
                                                            state: { 
                                                                selectionDetails: msg.templateQuotationDetails.selectionDetails, 
                                                                quoteDetails: msg.templateQuotationDetails.quoteDetails, 
                                                                isTemplate: true,
                                                                preserveChatHistory: true,
                                                                aiChatHistory: newHistory
                                                            } 
                                                        });
                                                        setTemplateFlowState({ active: false, quotationDetails: null, awaitingYesNo: false, awaitingClientName: false });
                                                    } else if (onSelectClient) {
                                                        onSelectClient(msg.clientDetails);
                                                        setAiChatHistory(prev => [...prev, {
                                                            id: Date.now().toString(),
                                                            sender: 'ai',
                                                            isSuccess: true,
                                                            text: `Client "${msg.clientDetails.client_name}" automatically selected for this quote.`,
                                                            timestamp: new Date().toISOString()
                                                        }]);
                                                    }
                                                }}
                                            >
                                                <Check size={16} /> Add to Quote
                                            </button>
                                        </div>
                                    ) : msg.isQuotationCard ? (
                                        <div className="bg-white border border-orange-200 rounded-lg p-3 text-sm shadow-sm w-full max-w-md">
                                            <h4 className="font-bold text-orange-800 mb-2 border-b border-orange-100 pb-1 flex justify-between">
                                                <span>Quotation Details</span>
                                                <span className="text-orange-600 font-mono bg-orange-50 px-1 rounded">{msg.quotationDetails.project.project_id}</span>
                                            </h4>
                                            
                                            <div className="max-h-[150px] overflow-y-auto mb-2 pr-1 custom-scrollbar">
                                                {msg.quotationDetails.selectionDetails.map((sel, idx) => (
                                                    <div key={idx} className="flex justify-between items-center text-xs mb-1 bg-slate-50 p-1.5 rounded">
                                                        <span className="font-medium text-slate-700 truncate mr-2" title={sel.selectionName}>{sel.selectionName}</span>
                                                        <span className="text-slate-600 whitespace-nowrap font-semibold">₹{parseFloat(sel.offerPrice || 0).toLocaleString()}</span>
                                                    </div>
                                                ))}
                                                {msg.quotationDetails.selectionDetails.length === 0 && (
                                                    <div className="text-slate-500 italic text-xs p-1">No selections found.</div>
                                                )}
                                            </div>

                                            <div className="bg-orange-50 p-2 rounded mb-2">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-orange-700">Total List Price:</span>
                                                    <span className="text-orange-900 font-medium">₹{msg.quotationDetails.totalListPrice.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between text-xs font-bold">
                                                    <span className="text-orange-800">Total Offer Price:</span>
                                                    <span className="text-orange-900">₹{msg.quotationDetails.totalOfferPrice.toLocaleString()}</span>
                                                </div>
                                            </div>

                                            <div className="flex gap-2 mt-2">
                                                <button 
                                                    className="flex-1 bg-white border border-orange-600 text-orange-700 hover:bg-orange-50 font-medium py-1.5 px-2 rounded-md transition-colors flex items-center justify-center gap-1"
                                                    onClick={() => navigate(`/user/viewProjects?project_id=${msg.quotationDetails.project.project_id}`)}
                                                >
                                                    Preview
                                                </button>
                                                <button 
                                                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-1.5 px-2 rounded-md transition-colors flex items-center justify-center gap-1"
                                                    onClick={() => navigate(`/user/create-quote/${msg.quotationDetails.project.client_id}/${encodeURIComponent(msg.quotationDetails.project.client_name)}/${msg.quotationDetails.project.id}`, { state: { selectionDetails: msg.quotationDetails.selectionDetails, quoteDetails: msg.quotationDetails.quoteDetails } })}
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                            <button 
                                                className="w-full mt-2 bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold py-2 px-3 rounded-md transition-colors flex items-center justify-center gap-2"
                                                onClick={() => {
                                                    setTemplateFlowState({ active: true, quotationDetails: msg.quotationDetails, awaitingYesNo: true, awaitingClientName: false });
                                                    setAiChatHistory(prev => [...prev, {
                                                        id: Date.now().toString(),
                                                        sender: 'ai',
                                                        isTemplateConfirmation: true,
                                                        isOverwriteWarning: window.location.pathname.includes('/user/create-quote'),
                                                        text: `Do you want to create the quotation for the same client (${msg.quotationDetails.project.client_name})?`,
                                                        timestamp: new Date().toISOString()
                                                    }]);
                                                }}
                                            >
                                                Use this template
                                            </button>
                                            
                                            <div className="flex gap-2 mt-2">
                                                <button 
                                                    className="flex-1 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 font-medium py-1.5 px-2 rounded-md transition-colors flex items-center justify-center gap-1 text-sm"
                                                    onClick={() => {
                                                        const messageId = Date.now().toString() + Math.random().toString(36).substring(7);
                                                        setAiChatHistory(prev => [...prev, { id: messageId, sender: 'ai', text: `Downloading PDF...`, isDownloading: true, format: 'pdf', timestamp: new Date().toISOString() }]);
                                                        downloadPdf(msg.quotationDetails.project.project_id, `${Apiurl}user/downloadPdf`).then(() => {
                                                            setAiChatHistory(prev => prev.map(m => m.id === messageId ? { ...m, isDownloading: false, text: "Downloaded PDF successfully!" } : m));
                                                        }).catch(() => {
                                                            setAiChatHistory(prev => prev.map(m => m.id === messageId ? { ...m, isDownloading: false, text: "Failed to download PDF." } : m));
                                                        });
                                                    }}
                                                >
                                                    <FileText size={16} /> PDF
                                                </button>
                                                <button 
                                                    className="flex-1 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 font-medium py-1.5 px-2 rounded-md transition-colors flex items-center justify-center gap-1 text-sm"
                                                    onClick={() => {
                                                        const messageId = Date.now().toString() + Math.random().toString(36).substring(7);
                                                        setAiChatHistory(prev => [...prev, { id: messageId, sender: 'ai', text: `Downloading EXCEL...`, isDownloading: true, format: 'excel', timestamp: new Date().toISOString() }]);
                                                        setExcelDownloadId({ id: msg.quotationDetails.project.project_id, messageId });
                                                    }}
                                                >
                                                    <Table size={16} /> Excel
                                                </button>
                                            </div>
                                        </div>
                                    ) : msg.isTemplateConfirmation ? (
                                        <div className="bg-white border border-orange-200 rounded-lg p-4 text-sm shadow-sm w-full max-w-md">
                                            {msg.isOverwriteWarning && (
                                                <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded-r-md">
                                                    <div className="flex items-start">
                                                        <div className="flex-shrink-0">
                                                            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                        <div className="ml-3">
                                                            <h3 className="text-sm font-medium text-red-800">Overwrite Warning</h3>
                                                            <div className="mt-1 text-xs text-red-700">
                                                                <p>Applying this template will completely erase and overwrite your current quotation data.</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <p className="text-slate-700 font-medium mb-4">{msg.text}</p>
                                            <div className="flex gap-2">
                                                <button 
                                                    className="flex-1 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-bold py-1.5 rounded-md transition-colors"
                                                    onClick={() => {
                                                        setAiChatHistory(prev => [...prev, {
                                                            id: Date.now().toString(),
                                                            sender: 'user',
                                                            text: "Yes",
                                                            timestamp: new Date().toISOString()
                                                        }]);
                                                        // Load template for same client
                                                        const clientName = templateFlowState.quotationDetails.project.client_name;
                                                        const clientId = templateFlowState.quotationDetails.project.client_id;
                                                        navigate(`/user/create-quote/${clientId}/${encodeURIComponent(clientName)}/new`, { state: { selectionDetails: templateFlowState.quotationDetails.selectionDetails, quoteDetails: templateFlowState.quotationDetails.quoteDetails, isTemplate: true } });
                                                        setTemplateFlowState({ active: false, quotationDetails: null, awaitingYesNo: false, awaitingClientName: false });
                                                        onClose();
                                                    }}
                                                >
                                                    Yes
                                                </button>
                                                <button 
                                                    className="flex-1 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-bold py-1.5 rounded-md transition-colors"
                                                    onClick={() => {
                                                        setAiChatHistory(prev => [...prev, {
                                                            id: Date.now().toString(),
                                                            sender: 'user',
                                                            text: "No",
                                                            timestamp: new Date().toISOString()
                                                        }, {
                                                            id: Date.now().toString() + 'ai',
                                                            sender: 'ai',
                                                            text: "Please enter the client name.",
                                                            timestamp: new Date().toISOString()
                                                        }]);
                                                        setTemplateFlowState(prev => ({ ...prev, awaitingYesNo: false, awaitingClientName: true }));
                                                    }}
                                                >
                                                    No
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`whitespace-pre-wrap ${msg.tabularData && msg.tabularData.length > 0 ? 'w-full max-w-[100%]' : 'max-w-[85%]'} rounded-2xl px-4 py-3 text-[14.5px] leading-relaxed ${
                                            msg.sender === 'user' 
                                                ? 'bg-orange-600 text-white rounded-tr-sm shadow-md' 
                                                : msg.isError 
                                                    ? 'bg-red-50 text-red-700 border border-red-100 rounded-tl-sm shadow-sm'
                                                    : msg.isSuccess
                                                        ? 'bg-green-50 text-green-700 border border-green-100 rounded-tl-sm shadow-sm'
                                                        : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm shadow-sm'
                                        }`}>
                                            {msg.isDownloading ? (
                                                <div className="flex items-center gap-2 text-orange-600 font-medium">
                                                    <Loader2 size={16} className="animate-spin" />
                                                    <span>{msg.text}</span>
                                                </div>
                                            ) : msg.isGreeting ? <TypewriterText text={msg.text} /> : (
                                                msg.sender === 'user' ? (
                                                    <span>{msg.text}</span>
                                                ) : (
                                                        <span 
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: (msg.text || '')
                                                                    .replace(/### (.*?)(?=\n|$)/g, '<h3 class="text-[14px] font-bold text-slate-800 mt-2 mb-1 border-b border-slate-100 pb-1">$1</h3>')
                                                                    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>')
                                                                    .replace(/\*(.*?)\*/g, '<em class="text-slate-600 italic">$1</em>')
                                                                    .replace(/`([^`]+)`/g, '<code class="bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded text-[13px] font-mono border border-orange-100">$1</code>')
                                                                    .replace(/\n- (.*?)(?=\n|$)/g, '<li class="ml-4 list-disc marker:text-orange-500">$1</li>')
                                                                    .replace(/(?:\r\n|\r|\n)/g, '<br />')
                                                            }} 
                                                        />
                                                )
                                            )}
                                            {msg.tabularData && msg.tabularData.length > 0 && (
                                                <div className="mt-5 w-full">
                                                    <div className="flex flex-col space-y-4">
                                                        {msg.tabularData.map((row, i) => (
                                                            <div 
                                                                key={i} 
                                                                className={`relative flex flex-col rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all hover:shadow-md ${i % 2 === 0 ? 'bg-orange-50/30' : 'bg-white'}`}
                                                            >
                                                                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-400 to-orange-600"></div>
                                                                <div className="bg-gradient-to-r from-orange-50 to-transparent border-b border-orange-100 px-4 py-3 flex items-center gap-2">
                                                                    <div className="p-1.5 bg-orange-100 text-orange-600 rounded-lg shadow-sm">
                                                                        <Database size={16} />
                                                                    </div>
                                                                    <h4 className="font-bold text-slate-800 text-[13px] uppercase tracking-wide">
                                                                        {row.project_id || row.client_name || `Record Details #${i + 1}`}
                                                                    </h4>
                                                                </div>
                                                                <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-4">
                                                                    {Object.entries(row).map(([key, val], j) => {
                                                                        let displayVal = val;
                                                                        if (val !== null && val !== undefined) {
                                                                            if (typeof val === 'string' && (key.toLowerCase().includes('date') || key.toLowerCase().includes('created') || key.toLowerCase().includes('updated') || key.toLowerCase().includes('time'))) {
                                                                                const d = new Date(val);
                                                                                if (!isNaN(d.getTime())) {
                                                                                    const pad = (n) => n.toString().padStart(2, '0');
                                                                                    displayVal = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                                                                                }
                                                                            } else if (!isNaN(val) && val.toString().trim() !== '') {
                                                                                const numVal = parseFloat(val);
                                                                                displayVal = new Intl.NumberFormat('en-IN', {
                                                                                    minimumFractionDigits: numVal % 1 !== 0 ? 2 : 0,
                                                                                    maximumFractionDigits: 2
                                                                                }).format(numVal);
                                                                            }
                                                                        }
                                                                        
                                                                        return (
                                                                        <div key={j} className={`flex flex-col ${key.toLowerCase().includes('name') || key.toLowerCase().includes('client') || typeof val === 'string' && val.length > 30 ? 'col-span-2' : 'col-span-1'}`}>
                                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                                                {(() => {
                                                                                    const k = key.toLowerCase();
                                                                                    if (k.includes('name') || k.includes('user') || k.includes('client') || k.includes('by') || k.includes('with') || k.includes('manager')) return <User size={12} className="text-slate-400" />;
                                                                                    if (k.includes('price') || k.includes('total') || k.includes('mrp') || k.includes('amount') || k.includes('discount')) return <IndianRupee size={12} className="text-slate-400" />;
                                                                                    if (k.includes('date') || k.includes('created') || k.includes('updated') || k.includes('time')) return <CalendarClock size={12} className="text-slate-400" />;
                                                                                    if (k.includes('project') || k.includes('quotation') || k.includes('id') || k.includes('code') || k.includes('sku')) return <Hash size={12} className="text-slate-400" />;
                                                                                    if (k.includes('email')) return <Mail size={12} className="text-slate-400" />;
                                                                                    if (k.includes('phone') || k.includes('mobile')) return <Phone size={12} className="text-slate-400" />;
                                                                                    if (k.includes('status')) return <Info size={12} className="text-slate-400" />;
                                                                                    if (k.includes('role') || k.includes('designation')) return <Briefcase size={12} className="text-slate-400" />;
                                                                                    return <Tag size={12} className="text-slate-400" />;
                                                                                })()}
                                                                                {key.replace(/_/g, ' ')}
                                                                            </span>
                                                                            <span className="text-[13px] text-slate-800 font-semibold break-words whitespace-pre-wrap">
                                                                                {typeof val === 'string' && val.startsWith('P_') ? (
                                                                                    <button 
                                                                                        onClick={() => handleProcessPrompt(`Show me the details of quotation ${val}`)}
                                                                                        className="text-orange-600 hover:text-orange-700 hover:underline transition-colors focus:outline-none text-left break-all font-bold"
                                                                                    >
                                                                                        {displayVal}
                                                                                    </button>
                                                                                ) : (
                                                                                    displayVal
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {msg.suggestions && (
                                                <div className="mt-3 flex flex-col gap-2">
                                                    {msg.suggestions.map((suggestion, i) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => setPrompt(suggestion)}
                                                            className="text-left px-3 py-2 bg-slate-50 hover:bg-orange-50 text-slate-600 hover:text-orange-700 text-[11px] font-medium rounded-lg border border-slate-200 hover:border-orange-200 transition-all"
                                                            style={{ animation: `typingFadeIn 0.4s ease-out forwards ${i * 150}ms`, opacity: 0 }}
                                                        >
                                                            {suggestion}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {msg.timestamp && (
                                        <span className="text-[10px] text-slate-400 px-1">
                                            {new Date(msg.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {loading && (
                        <div className="flex justify-start">
                            <div className="bg-white p-3 rounded-2xl rounded-tl-sm border border-slate-100 shadow-sm max-w-[85%] w-[200px]">
                                <div className="flex gap-3 items-center">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 animate-pulse flex-shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-2 bg-slate-100 rounded animate-pulse" />
                                        <div className="h-2 bg-slate-100 rounded w-5/6 animate-pulse" />
                                    </div>
                                </div>
                                {loadingText && (
                                    <p className="text-[10px] text-slate-400 mt-2 italic">{loadingText}</p>
                                )}
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t border-slate-200 bg-white shrink-0">
                    <style dangerouslySetInnerHTML={{__html: `
                        @keyframes typingFadeIn {
                            0% { opacity: 0; transform: translateY(5px); }
                            100% { opacity: 1; transform: translateY(0); }
                        }
                    `}} />
                    <div className="relative">
                        <textarea
                            value={prompt}
                            onChange={(e) => {
                                setPrompt(e.target.value);
                                setWasDictated(false);
                                resetInactivityTimer();
                            }}
                            disabled={loading || isListening}
                            rows={3}
                            placeholder={isListening ? "Listening..." : "Describe your requirements (e.g. products, collections, pricing)..."}
                            className={`w-full p-3 text-xs border rounded-xl focus:outline-none focus:ring-2 bg-slate-50 pr-12 pb-10 transition-colors resize-y min-h-[100px] max-h-[400px] ${
                                isListening ? 'border-orange-400 ring-2 ring-orange-200' : 'border-slate-200 focus:ring-orange-500'
                            }`}
                        />
                        <div className="absolute left-3 bottom-3 pointer-events-none">
                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${prompt.trim() && prompt.trim().split(/\s+/).length > 1500 ? 'bg-red-100 text-red-600' : 'bg-slate-200/50 text-slate-500'}`}>
                                {prompt.trim() ? prompt.trim().split(/\s+/).length : 0} / 1500 words
                            </span>
                        </div>
                        <div className="absolute right-2 bottom-3">
                            <IconButton 
                                onClick={toggleListening} 
                                size="small"
                                sx={{ 
                                    color: isListening ? '#ea580c' : '#64748b',
                                    backgroundColor: isListening ? '#ffedd5' : 'transparent',
                                    '&:hover': { backgroundColor: isListening ? '#fed7aa' : '#f1f5f9' }
                                }}
                                className={isListening ? "animate-pulse" : ""}
                            >
                                {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                            </IconButton>
                        </div>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" checked={isStrictMatch} onChange={() => setIsStrictMatch(true)} className="accent-orange-600 text-orange-600 focus:ring-orange-600 w-3.5 h-3.5" style={{ accentColor: '#ea580c' }} />
                                <span className="font-medium text-slate-700">Strict Match</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" checked={!isStrictMatch} onChange={() => setIsStrictMatch(false)} className="accent-orange-600 text-orange-600 focus:ring-orange-600 w-3.5 h-3.5" style={{ accentColor: '#ea580c' }} />
                                <span className="font-medium text-slate-700">Similar Match (Fuzzy)</span>
                            </label>
                        </div>

                        <button
                            onClick={loading ? handleAbort : () => handleProcessPrompt()}
                            disabled={!loading && ((!prompt.trim() && !(templateFlowState.active && templateFlowState.awaitingClientName)) || isSuccess)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-xl shadow-sm transition-all duration-300 ${
                                loading
                                    ? 'bg-red-500 hover:bg-red-600 shadow-[0_0_15px_rgba(239,68,68,0.6)]'
                                    : isSuccess 
                                        ? 'bg-green-500 hover:bg-green-600 shadow-[0_0_15px_rgba(34,197,94,0.6)] transform scale-105' 
                                        : 'bg-orange-600 hover:bg-orange-700 disabled:opacity-40'
                            }`}
                        >
                            {loading ? (
                                <X size={14} />
                            ) : isSuccess ? (
                                <Check size={14} className="animate-bounce" />
                            ) : (
                                <Send size={12} />
                            )}
                            {loading ? 'Abort' : isSuccess ? 'Success!' : 'Request AI'}
                        </button>
                    </div>
                </div>
            </div>

            {previewData && (
                <AiPreviewModal
                    isOpen={!!previewData}
                    onClose={() => {
                        setPreviewData(null);
                        if (typeof onOpen === 'function') onOpen();
                    }}
                    payload={previewData.payload}
                    warnings={previewData.warnings}
                    clientName={previewData.clientName}
                    selections={selections}
                    onConfirm={(finalPayload) => {
                        if (typeof onConfirmPreview === 'function') {
                            onConfirmPreview(finalPayload, previewData.messageId);
                        } else {
                            navigate(`/user/create-quote`, {
                                state: {
                                    aiPendingPayload: finalPayload,
                                    aiChatHistory: aiChatHistory,
                                    preserveChatHistory: true
                                }
                            });

                            setPreviewData(null);
                        }
                        setPreviewData(null);
                    }}
                />
            )}
            {excelDownloadId && (
                <div style={{ display: 'none' }}>
                    <DownloadSummary 
                        project={excelDownloadId.id} 
                        sendData={() => {
                            if (excelDownloadId.messageId) {
                                setAiChatHistory(prev => prev.map(msg => msg.id === excelDownloadId.messageId ? { ...msg, isDownloading: false, text: "Downloaded EXCEL successfully!" } : msg));
                            }
                            setExcelDownloadId(null);
                        }} 
                    />
                </div>
            )}
        </Portal>
    );
}
